/*
Copyright 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-NevaiMeet-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type FC } from "react";
import { Button } from "@vector-im/compound-web";

import { useSetting } from "./settings";
import {
  callHistory,
  maxCallHistory,
  type CallHistoryEntry,
} from "../meet/MeetSettings";
import {
  clearCallHistory,
  removeCallHistoryEntry,
} from "../meet/features/useCallHistory";
import styles from "./MeetSettings.module.css";

function formatDuration(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s.toString().padStart(2, "0")}s`;
}

function formatDate(ts: number): string {
  return new Date(ts).toLocaleString();
}

const CallRow: FC<{ entry: CallHistoryEntry }> = ({ entry }) => (
  <div className={styles.session}>
    <div>
      <div>{entry.roomName || "Unnamed call"}</div>
      <div className={styles.small}>
        {formatDate(entry.startedAt)} &middot; {formatDuration(entry.durationMs)}{" "}
        &middot; {entry.participants} participant
        {entry.participants === 1 ? "" : "s"}
      </div>
    </div>
    <Button
      kind="tertiary"
      onClick={() => removeCallHistoryEntry(entry.id)}
    >
      Remove
    </Button>
  </div>
);

export const CallHistorySettingsTab: FC = () => {
  const [history, setHistory] = useSetting(callHistory);
  const [max, setMax] = useSetting(maxCallHistory);

  return (
    <div>
      <h3 className={styles.sectionTitle}>Local Call History</h3>
      <p className={styles.note}>
        A private log of calls you joined on this device. It never leaves your
        browser and is not shared with the server.
      </p>

      <div className={styles.row}>
        <label htmlFor="maxHistory">Keep at most</label>
        <select
          id="maxHistory"
          value={max}
          onChange={(e) => setMax(Number(e.target.value))}
        >
          <option value={10}>10 calls</option>
          <option value={25}>25 calls</option>
          <option value={50}>50 calls</option>
          <option value={100}>100 calls</option>
        </select>
      </div>

      {history.length === 0 ? (
        <p className={styles.small}>No calls recorded yet.</p>
      ) : (
        <div>
          {history.map((entry) => (
            <CallRow key={entry.id} entry={entry} />
          ))}
        </div>
      )}

      {history.length > 0 && (
        <Button kind="destructive" onClick={() => { clearCallHistory(); setHistory([]); }}>
          Clear history
        </Button>
      )}
    </div>
  );
};
